import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, ModalContent, Textarea } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { addClubMemberNote, listClubMemberNotes } from '../../services/clubMembershipService';

const ClubMemberNotesModal = ({ isOpen, onClose, clubId, playerId, playerName }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [body, setBody] = useState('');

  const loadNotes = useCallback(async () => {
    if (!isOpen || !clubId || !playerId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listClubMemberNotes(clubId, playerId);
      setNotes(rows);
    } catch (err) {
      console.error('Failed to load member notes:', err);
      setError(err.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [isOpen, clubId, playerId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAdd = async () => {
    const trimmed = body.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await addClubMemberNote(clubId, playerId, trimmed);
      setBody('');
      await loadNotes();
    } catch (err) {
      console.error('Failed to add member note:', err);
      setError(err.message || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium">
        <Heading level={3} className="mb-1">
          Notes
        </Heading>
        <Text className="mb-4 text-text-secondary">
          Admin-only record for {playerName || 'this player'}. Players cannot see these notes.
        </Text>

        {error ? (
          <p className="mb-3 text-sm text-danger">{error}</p>
        ) : null}

        <div className="mb-4 max-h-64 space-y-3 overflow-y-auto">
          {loading ? (
            <Typography variant="muted">Loading notes…</Typography>
          ) : notes.length === 0 ? (
            <Typography variant="muted">No notes yet.</Typography>
          ) : (
            notes.map((note) => (
              <div
                key={note.note_id}
                className="rounded-lg border border-border-medium bg-surface-subtle p-3"
              >
                <p className="text-sm text-text-primary whitespace-pre-wrap">{note.body}</p>
                <p className="mt-2 text-xs text-text-muted">
                  {note.author_name || 'Admin'} ·{' '}
                  {note.created_at
                    ? new Date(note.created_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''}
                </p>
              </div>
            ))
          )}
        </div>

        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Add a note for other admins…"
          aria-label="New member note"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={saving || !body.trim()}
          >
            {saving ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ClubMemberNotesModal;
